/**
 * Implements a HitBTC datafeed
 */
import { ErrorCallback, HistoryCallback, LibrarySymbolInfo, PeriodParams, ResolutionString, SearchSymbolsCallback } from '../charting_library/datafeed-api';
import { ITicker } from '@kattana-io/common';
import { CexAPI, KattanaAPI } from '../../../store/createStore';

interface IHash {
  [details: string]: string;
}

const mapping: IHash = {
  1: 'M1',
  3: 'M3',
  5: 'M5',
  15: 'M15',
  30: 'M30',
  60: 'H1',
  '1D': 'D1',
  D: 'D1',
  '1W': 'D7',
};
const supported_resolutions = [
  '1', '3', '5', '15', '30', '60', '1D', '1W'
];

/* eslint-disable no-restricted-syntax */
class HitbtcDatafeed {
  hitbtcHost: string;
  debug: any;
  symbolsPairs: any;
  pairsSymbols: any;
  barsInterval: any;
  exchange: string;
  ticker: ITicker | null;

  //   symbolsPairs: any;
  constructor(options: any = {}) {
    this.hitbtcHost = 'https://broker.kattana.trade';
    this.debug = options.debug || false;
    this.symbolsPairs = {};
    this.pairsSymbols = {};
    this.barsInterval = null;
    this.exchange = 'hitbtc';
    this.ticker = null;
  }

  public isIntervalSupported = (interval: string): boolean =>
    supported_resolutions.includes(interval);

  /**
     * Request remote symbols
     */
  findSymbols = async (symbolId: number, query: string) => {
    const tickers = await KattanaAPI.tickers({exchange: this.exchange, id: symbolId, searchRaw: query });
    if (!tickers) {
      return [];
    }
    const symbols: any[] = tickers.map((item: ITicker) => {
      const symbol = item.name;
      const [base_asset, quote_asset] = symbol.split('/');

      return ({
        symbol: symbol.toUpperCase(),
        symbolName: `${base_asset}${quote_asset}`.toUpperCase(), // well, this is how we get input
        base_asset,
        quote_asset,
        base_address: item.base_address,
        quote_address: item.quote_address,
        base_decimals: item.base_decimals,
        quote_decimals: item.quote_decimals,
        precision: item.quote_decimals,
        price: item.price_a,
        ticker: item,
      });
    });

    return symbols;
  }

  hitbtcOHLCV = async (
    symbol: string,
    interval: any,
    startTime: any,
    endTime: any,
    limit: number
  ) => {
    const sym = symbol.split('/').join('');
    return await CexAPI.fetchKLines(this.exchange, sym, interval, startTime, endTime, limit);
  };

  hitbtcLastCandle = async (symbol: string, interval: string) => {
    const sym = symbol.split('/').join('');
    return CexAPI.fetchHitbtcLastCandle(sym, interval);
  };

  onReady = async (callback: Function) => {
    try {
      const symbolId = -1;
      const symbols = await this.findSymbols(symbolId, '');
      symbols.forEach(({ symbol, baseAsset, quoteAsset }: any) => {
        const pair = `${baseAsset}-${quoteAsset}`;
        this.symbolsPairs[symbol] = pair;
        this.pairsSymbols[pair] = symbol;
      });
      callback({
        supports_marks: false,
        supports_timescale_marks: false,
        supports_time: true,
        supported_resolutions,
      });
    } catch (err) {
      console.error(err);
    }
  };

  searchSymbols = async (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResultReadyCallback: SearchSymbolsCallback
  ) => {
    const input = userInput.toUpperCase();
    const symbolId = -1;
    const symbols = await this.findSymbols(symbolId, input);
    onResultReadyCallback(
      symbols.map((symbol: any) => ({
        symbol: symbol.symbol,
        full_name: symbol.symbol,
        description: `${symbol.base_asset} / ${symbol.quote_asset}`,
        pair: `${symbol.quoteAsset}-${symbol.base_asset}`,
        ticker: symbol.symbol,
        exchange: this.exchange,
        type: 'stock'
      }))
    );
  }

  async resolveSymbol(
    symbolName: string,
    onSymbolResolvedCallback: Function,
    onResolveErrorCallback: Function
  ) {
    if (this.debug) {console.log('👉 resolveSymbol:', symbolName);}
    const searchById = symbolName.includes('{id}:');

    const comps = symbolName.split(':');
    // eslint-disable-next-line no-param-reassign
    symbolName = (comps.length > 1 ? comps[1] : symbolName).toUpperCase();

    let symbolId = -1;
    if (searchById) {
      symbolId = Number(comps[1]);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const pricescale = ({ tickSize }: any) => {
      return Math.round(1 / parseFloat(tickSize));
    };

    const [symbol] = await this.findSymbols(symbolId, symbolName);

    if (symbol) {
      if (symbol.ticker) {
        this.ticker = (symbol as any).ticker;
      }

      setTimeout(() => {
        onSymbolResolvedCallback({
          name: symbol.symbol,
          description: `${symbol.base_asset} / ${symbol.quote_asset}`,
          pair: `${symbol.quoteAsset}-${symbol.baseA_aset}`,
          ticker: symbol.symbol,
          session: '24x7',
          minmov: 1,
          pricescale: 10 ** symbol.base_decimals,
          timezone: 'UTC',
          has_intraday: true,
          has_daily: true,
          has_weekly_and_monthly: true,
          currency_code: symbol.base_asset,
          exchange: this.exchange,
        });
      }, 0);
    } else {
      onResolveErrorCallback('not found');
    }
  }

  getInterval = (resolution: any) => mapping[resolution];

  getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: ErrorCallback
  ) {
    if (this.debug) {
      console.log('👉 getBars:', symbolInfo.name, resolution);
      console.log('First:', periodParams.firstDataRequest);
      console.log('From:', periodParams.from, new Date(periodParams.from * 1000).toUTCString());
      console.log('To:  ', periodParams.to, new Date(periodParams.to * 1000).toUTCString());
    }

    const interval = this.getInterval(resolution);

    if (!interval) {
      onError('Invalid interval');
    }

    let totalKlines: any[] = [];

    const finishKlines = () => {
      if (this.debug) {
        console.log('📊:', totalKlines.length);
      }

      if (totalKlines.length === 0) {
        onResult([], {
          noData: true,
        });
      } else {
        onResult(
          totalKlines.map((kline) => ({
            time: Date.parse(kline.timestamp),
            close: parseFloat(kline.close),
            open: parseFloat(kline.open),
            high: parseFloat(kline.max),
            low: parseFloat(kline.min),
            volume: parseFloat(kline.volume),
          })),
          {
            noData: false,
          }
        );
      }
    };

    // eslint-disable-next-line no-shadow
    const getKlines = (from: any, to: any) => {
      this.hitbtcOHLCV(symbolInfo.name, interval, from, to, 1000)
        .then((klines) => {
          totalKlines = totalKlines.concat(klines);
          if (klines.length === 1000) {
            const lastCandle = klines[klines.length - 1];
            const { timestamp } = lastCandle;
            // eslint-disable-next-line no-param-reassign
            from = Date.parse(timestamp) + 1;
            if (Number.isNaN(from)) {
              finishKlines();
            } else {
              getKlines(from, to);
            }
          } else {
            finishKlines();
          }
          return null;
        })
        .catch((err) => {
          console.error(err);
          onError('Some problem');
        });
    };

    // eslint-disable-next-line no-param-reassign
    periodParams.from *= 1000;
    // eslint-disable-next-line no-param-reassign
    periodParams.to *= 1000;

    getKlines(periodParams.from, periodParams.to);
  }

  // eslint-disable-next-line no-unused-vars
  subscribeBars(
    symbolInfo: any,
    resolution: any,
    onRealtimeCallback: Function,
    subscriberUID: any,
    onResetCacheNeededCallback: Function
  ) {
    if (this.debug) {console.log('👉 subscribeBars:', subscriberUID);}

    const { name } = symbolInfo;
    const interval = this.getInterval(resolution);
    this.barsInterval = setInterval(() => {
      this.hitbtcLastCandle(name, interval)
        .then((data: any) => {
          const kline = data[0];
          const bar = {
            time: Date.parse(kline.timestamp),
            close: parseFloat(kline.close),
            open: parseFloat(kline.open),
            high: parseFloat(kline.max),
            low: parseFloat(kline.min),
            volume: parseFloat(kline.volume),
          };
          onRealtimeCallback(bar);
          return null;
        })
        .catch((err: Error) => {
          console.error(err);
        });
    }, 15000);
  }

  unsubscribeBars(subscriberUID: any) {
    if (this.debug) {console.log('👉 unsubscribeBars:', subscriberUID);}
    clearInterval(this.barsInterval);
  }

  public getTicker = () => this.ticker;
}

export default HitbtcDatafeed;
